export const TA_GREEN = '#00AA6C';
// Supply encodes TA's 1-5 bubble score onto 10-100 with min-max normalization
// (raw = (score - 1) / 4 * 90 + 10); invert it here and clamp to TA's 1-5 range.
export const toTADisplayValue = (rawScore: number): number => Math.min(5, Math.max(1, ((rawScore - 10) / 90) * 4 + 1));

export const toTADisplayScore = (rawScore: number): string => toTADisplayValue(rawScore).toFixed(1);

// TripAdvisor renders 5 bubbles, rounded to the nearest half (e.g. 4.7 → 4.5 → 4 full + 1 half).
export type TABubbleFill = 'full' | 'half' | 'empty';

// Per-bubble fill state (length 5) from a 0-5 display value, rounded to nearest half-bubble
export const toTABubbleFills = (displayValue: number): TABubbleFill[] => {
  const rounded = Math.round(displayValue * 2) / 2;
  return Array.from({ length: 5 }, (_, i) => {
    if (rounded >= i + 1) return 'full';
    if (rounded >= i + 0.5) return 'half';
    return 'empty';
  });
};

export type TARatingInterval = {
  /** Inclusive lower bound on TA's 1-5 bubble scale, compared against the 1-decimal displayed score. */
  minDisplayValue: number;
  name: string;
  translationKey: string;
};

// TripAdvisor's five official labels, bucketed on the same 1-decimal bubble score we render
// (toTADisplayScore) so the label can never disagree with the number shown next to it
// (e.g. 2.8 → "Average", matching TA's own site). Cut points are TA's half-bubble rounding
// boundaries; sorted by minDisplayValue descending — matching is on the lower bound only.
export const taRatingIntervals: TARatingInterval[] = [
  { minDisplayValue: 4.5, name: 'excellent', translationKey: 'vacation.common.rating.excellent' },
  { minDisplayValue: 3.5, name: 'verygood', translationKey: 'vacation.common.rating.verygood' },
  { minDisplayValue: 2.5, name: 'average', translationKey: 'vacation.common.rating.average' },
  { minDisplayValue: 1.5, name: 'poor', translationKey: 'vacation.common.rating.poor' },
  { minDisplayValue: 1, name: 'terrible', translationKey: 'vacation.common.rating.terrible' },
];

// toTADisplayValue clamps to [1, 5], so every numeric raw score lands in a band (NaN in none).
export const getTARatingInterval = (rawScore: number): TARatingInterval | undefined => {
  const displayValue = Number(toTADisplayScore(rawScore));
  return taRatingIntervals.find(({ minDisplayValue }) => displayValue >= minDisplayValue);
};

export const TA_TRIP_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  NONE: 'vacation.common.rating.type.all',
  COUPLES: 'vacation.common.rating.type.couple',
  FAMILY: 'vacation.common.rating.type.family',
  // TrustYou had no FRIENDS type, so this key is TA-only (requested from the copy team in MPH-763).
  FRIENDS: 'vacation.common.rating.type.friends',
  BUSINESS: 'vacation.common.rating.type.business',
  SOLO: 'vacation.common.rating.type.solo',
};

// TripAdvisor hotel-page anchor that lands straight on the reviews block.
export const TA_REVIEWS_ANCHOR = '#REVIEWS';

/**
 * Hotel-level TripAdvisor review page resolved by hotel-reviews (MPH-903). The field is optional
 * upstream (absent when it could not be resolved) and identical on every page of a paginated
 * result, so any loaded page will do. Only http(s) URLs are accepted — the value ends up in an
 * `href`. Returns the URL pointed at TA's `#REVIEWS` anchor (MPH-763).
 */
export const getTripAdvisorHotelReviewsUrl = (
  pages?: ReadonlyArray<TripadvisorApiResponse | undefined | null>
): string | undefined => {
  const url = pages?.find((page) => page?.data?.hotelReviewsUrl)?.data.hotelReviewsUrl;
  if (!url || !/^https?:\/\//i.test(url)) return undefined;
  return url.includes('#') ? url : `${url}${TA_REVIEWS_ANCHOR}`;
};
